import { Chip, Skeleton, Typography, TypographyProps } from '@ethings/ui-toolkit';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useOdometerReadingQuery } from '../../hooks';
import { formatDistanceString } from '../../util';
import { useBoolFlag } from '../flags';
import { useLatestServiceHistoryQuery, useNextServiceQuery, useServiceMetricsQuery } from '../service-history-card/service.utils';

const ODOMETER_FEATURE_FLAG = 'portals-odometer-gps-fallback-feature';

interface Props {
  thingId: string;
  variant?: TypographyProps['variant'];
  loadingComponent?: ReactNode;
  currentOdometerMeters?: number;
}

const DISTANCE_FEATURE = 'distance/primary';

export const NextServiceDistanceDisplay: FC<Props> = ({ thingId, variant = 'inherit', loadingComponent: Loading, currentOdometerMeters: suppliedOdometerMeters }) => {
  const { t } = useTranslation(['common']);
  const flagEnabled = useBoolFlag(ODOMETER_FEATURE_FLAG);
  const { data: latestService, isLoading: isLoadingServiceHistory } = useLatestServiceHistoryQuery({ thingId });
  const serviceHistoryId = latestService?.serviceHistoryId ?? '';
  const { data: nextService, isLoading: isLoadingNextService } = useNextServiceQuery({ serviceHistoryId }, Boolean(serviceHistoryId));
  const { data: serviceMetrics, isLoading: isLoadingServiceMetrics } = useServiceMetricsQuery({ thingId }, Boolean(serviceHistoryId));
  const { data: odometerReading, isLoading: isLoadingOdometerReading } = useOdometerReadingQuery(
    thingId,
    { enabled: Boolean(serviceHistoryId) },
  );

  const distanceMetric = serviceMetrics?.find((metric) => metric.featureMapping?.some((mapping) => mapping.feature === DISTANCE_FEATURE));
  const distanceMapping = distanceMetric?.featureMapping?.find((mapping) => mapping.feature === DISTANCE_FEATURE);
  const value = distanceMetric ? nextService?.serviceMetrics?.[distanceMetric.serviceMetricId] : undefined;
  const targetMeters = typeof value === 'number' ? value / (distanceMapping?.unitConversionMultiplier || 1) : undefined;
  // An explicitly supplied value always wins (backward compat for callers still on the old contract).
  // Otherwise, flag on: full calibrated + gps-fallback resolution; flag off: restore pre-flag behavior
  // (distance/primary raw value only, never gps-derived or calibrated).
  const currentOdometerMeters = typeof suppliedOdometerMeters === 'number'
    ? suppliedOdometerMeters
    : flagEnabled
      ? odometerReading?.calibratedValueMeters
      : (odometerReading?.feature === DISTANCE_FEATURE ? odometerReading.rawValueMeters : undefined);
  const isLoading = isLoadingServiceHistory || isLoadingNextService || isLoadingServiceMetrics || isLoadingOdometerReading;

  if (isLoading) {
    return !Loading ? <Typography variant={variant}><Skeleton /></Typography> : Loading;
  }

  if (typeof targetMeters !== 'number' || typeof currentOdometerMeters !== 'number') {
    return <Typography variant={variant} />;
  }

  const distanceMeters = targetMeters - currentOdometerMeters;
  if (distanceMeters < 0) {
    return <Chip
      color="error"
      label={t('common:component.next-service-distance.overdue', {
        distance: formatDistanceString(distanceMeters, undefined, 'short', true),
      })}
      size="small"
      variant="light"
    />;
  }

  return <Typography variant={variant}>{formatDistanceString(distanceMeters, undefined, 'short')}</Typography>;
};
